import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import type { Condition } from "@/types";

import { ActionRecord } from "../ActionRecord";
import { type ActionQueryScope } from "../ActionService";

/**
 * Fetch every action visible in the given scope ("org" for the caller's
 * organization, "actionhub" for actions published to the Action Hub), or
 * only those matching `condition` when one is given; the server filters.
 *
 * This drains every page, so it is for pickers and option lists that need
 * the whole set. A table pages with `useActionsPage` instead. `enabled`
 * false holds the fetch, for a caller that needs the list in one scope only.
 */
export function useActions(
  scope: ActionQueryScope,
  condition?: Condition,
  options: { enabled?: boolean } = {},
) {
  const { actionService } = useDomainServices();
  const { currentOrganization } = useAuth();

  return useQuery<ActionRecord[], Error>({
    queryKey:
      condition === undefined
        ? ["actions", scope, currentOrganization?.org_id]
        : ["actions", scope, currentOrganization?.org_id, condition],
    queryFn({ signal }) {
      if (currentOrganization === null) {
        return Promise.reject(new Error("Requires login"));
      }

      return actionService.listActions(scope, {
        abortSignal: signal,
        orgId: currentOrganization.org_id,
        condition,
      });
    },
    enabled: currentOrganization !== null && options.enabled !== false,
    // A changed filter keeps the last rows on screen until the new ones land.
    placeholderData: condition === undefined ? undefined : keepPreviousData,
  });
}
